import { z } from 'zod';
import { baseListQuerySchema, paginationResponseSchema, SortOrder, sortOrderSchema, } from './helpers';
import { knownTotalQuerySchema } from './known-total';
import { organizationIdSchema } from './organizations';
import { severitySchema } from './shared/severity';
import { ColumnDataTypes, createTableQuerySchema, DATE_RANGE_OPERATORS, FilterOperators, FiltersSchema, FilterValueSchema, } from './table';
import { filterOptionItemSchema } from './mcp';
import { ENDPOINT_FILTER_FIELD, ENDPOINT_FILTER_OPERATORS } from './endpoints';
import { loggedInUserCountsSchema, loggedInUserMatchSchema, } from './shadow-ai-logged-in-user';
export const shadowAiCategorySchema = z.enum([
    'chatbot',
    'code_assist',
    'llm_api',
    'llm_local',
]);
export const categoryLabels = {
    chatbot: 'Chatbot',
    code_assist: 'Code Assistant',
    llm_api: 'LLM API',
    llm_local: 'Local LLM',
};
/**
 * Human-readable labels for every Shadow AI category key stored or shown in the product.
 * Keys are snake_case (e.g. code_assist). Used for table cells, filters, and CSV-adjacent UI.
 */
export const shadowAiCategoryLabels = {
    ...categoryLabels,
    agent: 'Agent',
    assistant: 'Assistant',
    ai_browser: 'AI Browser',
    automation: 'Automation',
    browser_extension: 'Browser Extension',
    code_repository: 'Code Repository',
    design_ai: 'Design (AI features)',
    ide: 'IDE',
    image_gen: 'Image Gen',
    llm: 'LLM',
    mcp_server: 'MCP Server',
    audio_gen: 'Audio Gen',
    video_gen: 'Video Gen',
    agent_dev_platform: 'Agent Dev Platform',
    search_engine: 'Search Engine',
    tool: 'Tool',
    other: 'Other',
};
function formatUnknownShadowAiCategoryLabel(category) {
    const trimmed = category.trim();
    if (trimmed === '') {
        return category;
    }
    return trimmed
        .split('_')
        .map(segment => {
        const lower = segment.toLowerCase();
        if (lower === 'llm')
            return 'LLM';
        if (lower === 'api')
            return 'API';
        if (lower === 'ai')
            return 'AI';
        if (lower === 'id')
            return 'ID';
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(' ');
}
/** Resolves a stored category key to the same label used in filters and tables. */
export function getShadowAiCategoryLabel(category) {
    return (shadowAiCategoryLabels[category] ??
        formatUnknownShadowAiCategoryLabel(category));
}
/**
 * Kind of local artifact the agent read the logged-in account from. Metadata-only
 * (account hints) — never decrypted secrets or credential values (KIR-2916).
 */
export const loggedInUserSourceSchema = z.enum([
    'config_file',
    'token_file',
    'cookie',
    'localstorage',
    'indexeddb',
    'keychain_metadata',
]);
/**
 * Why a Shadow AI detection has no LoggedInUser. Emitted by the agent in place of
 * a {@link loggedInUserSchema} value.
 */
export const loggedInUserReasonSchema = z.enum([
    'signed_out',
    'unsupported',
    'no_local_artifact',
]);
/**
 * Normalised logged-in account extracted from a Shadow AI tool's local artifacts.
 * `accountEmail` is the primary identity; `accountId` is the fallback stable id when
 * the tool exposes no email.
 */
export const loggedInUserSchema = z.object({
    accountEmail: z.string().nullable(),
    accountId: z.string().nullable(),
    tenant: z.string().nullable(),
    displayName: z.string().nullable(),
    provider: z.string(),
    source: loggedInUserSourceSchema,
});
// Re-exported (imported above) so existing `contracts/shadow-ai` consumers keep
// working; the canonical definitions live in the leaf module to avoid an import
// cycle with the list-item / posture contracts (KIR-2916).
export { loggedInUserCountsSchema, loggedInUserMatchSchema, };
/**
 * Normalises an email for comparison: trims, lowercases, and strips any `+alias`
 * suffix from the local part. Malformed input (no `@`) is compared verbatim.
 */
function normalizeEmailForMatch(email) {
    const trimmed = email.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1) {
        return trimmed;
    }
    const local = trimmed.slice(0, at);
    const domain = trimmed.slice(at + 1);
    const plus = local.indexOf('+');
    const normalizedLocal = plus === -1 ? local : local.slice(0, plus);
    return `${normalizedLocal}@${domain}`;
}
/** Returns the lowercased domain of an email, or null when it has no domain part. */
function emailDomain(email) {
    const trimmed = email.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1) {
        return null;
    }
    return trimmed.slice(at + 1);
}
/**
 * Classifies a Shadow AI detection's logged-in account against the Kirin user it is
 * attributed to. See {@link loggedInUserMatchSchema} for the semantics of each value.
 * Matching is case-insensitive and tolerant of `+aliases`.
 */
export function classifyLoggedInUserMatch(loggedInUserEmail, kirinUserEmail) {
    if (!loggedInUserEmail || loggedInUserEmail.trim() === '') {
        return 'unknown';
    }
    if (!kirinUserEmail || kirinUserEmail.trim() === '') {
        return 'personal';
    }
    if (normalizeEmailForMatch(loggedInUserEmail) ===
        normalizeEmailForMatch(kirinUserEmail)) {
        return 'matched';
    }
    const loggedInDomain = emailDomain(loggedInUserEmail);
    const kirinDomain = emailDomain(kirinUserEmail);
    if (loggedInDomain && kirinDomain && loggedInDomain === kirinDomain) {
        return 'corporate';
    }
    return 'personal';
}
export const shadowAiFindingSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    sources: z.array(z.string()).optional(),
    paths: z.array(z.string()).optional(),
    version: z.string().optional(),
    lastUsed: z.string().datetime({ offset: true }).optional(),
    addedAt: z.string().datetime({ offset: true }).optional(),
    isRunning: z.boolean().optional(),
    extras: z.record(z.string(), z.string()).optional(),
    loggedInUser: loggedInUserSchema.nullish(),
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
});
/**
 * One ancestor of a detected process (KIR-4651).
 *
 * Only `pid` is required: each field is a separate OS read that can fail on its
 * own, so a node with holes is normal, not malformed. Field names mirror
 * {@link shadowAiRuntimeFindingSchema}, not the agent's Go field names.
 */
export const shadowAiProcessAncestorSchema = z.object({
    pid: z.number().int(),
    /**
     * Current agents always send this — a process only enters a chain once it
     * has been read, and the root's parent is a real 0. Optional so a node from
     * an agent that omitted it still parses.
     */
    ppid: z.number().int().optional(),
    /** Executable's accounting name, e.g. `zsh`. */
    name: z.string().optional(),
    binaryPath: z.string().optional(),
    /** OS account owning the ancestor, not the account signed in to any tool. */
    user: z.string().optional(),
    /**
     * Full argv, already scrubbed of credential-looking values at the agent by
     * `util.RedactCommandLineSecrets` (KIR-4273) — the same pass the flagged
     * process's own `commandLine` gets. Scrubbing is pattern-based, so treat
     * this as reduced-risk rather than guaranteed clean.
     */
    commandLine: z.string().optional(),
    startedAt: z.string().datetime({ offset: true }).optional(),
});
export const shadowAiRuntimeFindingSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string(),
    pid: z.number().int(),
    ppid: z.number().int().optional(),
    port: z.number().int().optional(),
    user: z.string().optional(),
    binaryPath: z.string().optional(),
    commandLine: z.string().optional(),
    parentName: z.string().optional(),
    isRoot: z.boolean().optional(),
    extras: z.record(z.string(), z.string()).optional(),
    /**
     * Ancestor chain (KIR-4651), detected process first then each parent. May
     * stop short of PID 1 — an unprivileged macOS daemon cannot read launchd.
     *
     * Unbounded on purpose: this schema gates the whole report, so a `.max()`
     * would reject every finding in the POST over one long chain (same reasoning
     * as `signerStatus` below). Capped at the column instead, by
     * `MAX_PROCESS_ANCESTRY_NODES`.
     */
    ancestry: z.array(shadowAiProcessAncestorSchema).optional(),
    startedAt: z.string().datetime({ offset: true }),
    loggedInUser: loggedInUserSchema.nullish(),
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
});
/**
 * Code-signature verdict for the process that owns a connection (KIR-4588).
 * Absent/null means "could not determine" — the platform cannot report
 * signatures, the process exited first, or the lookup failed. That is not
 * the same as `unsigned`, and must never be rendered as a warning.
 *
 * - `verified` — the OS validated the signature against the binary.
 * - `platform` — an OS-vendor binary (Apple platform binary, Microsoft).
 * - `adhoc`    — signed with no identity behind it.
 * - `unsigned` — unsigned, or a signature that failed validation.
 */
export const signatureStatusSchema = z.enum([
    'verified',
    'platform',
    'adhoc',
    'unsigned',
]);
/**
 * Ingest contract for one network finding. The agent has no reporter-side DTO
 * — it marshals `types.SNIFinding` directly — so every key must match that
 * struct's json tag exactly. Zod strips unknown keys rather than erroring, so
 * a missing key silently drops the value; KIR-4588 found `processPath`,
 * `ppid`, `parentProcessName` and `linkedToolId` lost that way while the
 * service was already reading them. Everything but domain/source is optional:
 * older agents predate these, and most are platform-specific.
 */
export const shadowAiSniFindingSchema = z.object({
    domain: z.string(),
    source: z.string(), // "tls", "dns", "streaming", "etw_dns"
    pid: z.number().int().optional(),
    ppid: z.number().int().optional(),
    processName: z.string().optional(),
    processPath: z.string().optional(),
    parentProcessName: z.string().optional(),
    /**
     * See {@link signatureStatusSchema}; absent when undeterminable.
     *
     * Deliberately a plain string rather than the enum. This schema is the
     * ingest gate for the *whole* report, so a verdict a newer agent knows and
     * this backend does not would 400 the entire POST - every SNI finding in
     * it, plus `detected` and `running`. `signer_status` is a free-text column
     * and the read path already degrades an unrecognised verdict to null
     * (`toSignatureStatus`), so store what the agent sent and normalise on the
     * way out.
     */
    signerStatus: z.string().optional(),
    /** Signing authority; only sent alongside a verified/platform status. */
    signer: z.string().optional(),
    /** OS account owning the process, not the account signed in to the tool. */
    user: z.string().optional(),
    /** Remote endpoint; darwin TLS findings only. */
    dstIp: z.string().optional(),
    /**
     * Bounded because the column is int4 and `bulkUpsertSniEvents` writes the
     * whole report in one multi-row INSERT, so a single out-of-range value
     * would fail the entire batch rather than one finding. The agent reads
     * this from a uint16 and cannot exceed it; the bound is here because this
     * schema is the trust boundary, the same reason every string field is
     * length-capped on the way in.
     */
    port: z.number().int().min(0).max(65535).optional(),
    linkedToolId: z.string().optional(),
    ja4: z.string().optional(),
    tlsVersion: z.string().optional(),
    isProgrammatic: z.boolean().optional(),
    isStreaming: z.boolean().optional(),
    isAsymmetric: z.boolean().optional(),
    isLongLived: z.boolean().optional(),
    packetsIn: z.number().int().optional(),
    packetsOut: z.number().int().optional(),
    bytesIn: z.number().int().optional(),
    bytesOut: z.number().int().optional(),
    byteRatio: z.number().optional(),
    durationMs: z.number().int().optional(),
});
export const shadowAiReportRequestSchema = z.object({
    detected: z.array(shadowAiFindingSchema).optional(),
    running: z.array(shadowAiRuntimeFindingSchema).optional(),
    sni: z.array(shadowAiSniFindingSchema).optional(),
});
/**
 * Ingest bounds for browser-agent reports (KIR-4703 review) — well above what an
 * honest agent sends, so a hostile key can't push unbounded rows or day buckets.
 * The agent caps its report at {@link BROWSER_AGENT_MAX_SITES_PER_REPORT} too.
 */
export const BROWSER_AGENT_MAX_SITES_PER_REPORT = 5000;
const BROWSER_AGENT_MAX_DAILY_VISITS = 100000;
const BROWSER_AGENT_MAX_HOST_LENGTH = 255;
const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/** A bare host: no scheme, path, query, fragment, userinfo or whitespace. */
const BARE_HOST_PATTERN = /^[^\s/?#@\\]+$/;
const browserAgentSiteHostSchema = z
    .string()
    .min(1)
    .max(BROWSER_AGENT_MAX_HOST_LENGTH)
    .regex(BARE_HOST_PATTERN, 'Must be a bare host (no scheme, path or query)');
/**
 * One browser-agent site observation (KIR-4703 / KIR-4704, spec 70509).
 * `siteHost` is the FULL host (subdomains distinct); no path/query, no content.
 * `agentType` is the reach shape (drives enforceability downstream). `visitsByDay`
 * is a day→count map ('YYYY-MM-DD' → n) merged server-side for the rolling-30d
 * sum. `lastSeen` is the agent's observed time; the backend keeps the latest
 * across sources (GREATEST).
 */
export const browserAgentSiteReportItemSchema = z.object({
    browser: z.string().max(32),
    agentType: z.enum([
        'chrome_via_cli',
        'chrome_via_browser',
        'claude_code_cli',
    ]),
    siteHost: browserAgentSiteHostSchema,
    sources: z.array(z.string().max(32)).max(8),
    agentExtId: z.string().max(128).nullish(),
    visitsByDay: z
        .record(z.string().regex(DAY_KEY_PATTERN), z.number().int().min(0).max(BROWSER_AGENT_MAX_DAILY_VISITS))
        .nullish(),
    reachedVia: z.string().max(64).nullish(),
    sessionId: z.string().max(256).nullish(),
    sessionProxy: z.string().max(256).nullish(),
    lastSeen: z.string().datetime({ offset: true }).nullish(),
    extras: z
        .record(z.string().max(64), z.string().max(1024))
        .refine(e => Object.keys(e).length <= 32, 'At most 32 extras')
        .nullish(),
    // KIR-4703: a BLOCKED attempt (the CLI hook denied this nav), not a visit. The
    // backend does not upsert it into browser_agent_sites; it mints a 'prevented'
    // alert so a blocked site still surfaces on the Alerts page. visitsByDay then
    // carries the blocked-attempt count.
    denied: z.boolean().nullish(),
});
export const browserAgentActivityReportRequestSchema = z.object({
    sites: z
        .array(browserAgentSiteReportItemSchema)
        .max(BROWSER_AGENT_MAX_SITES_PER_REPORT),
});
/** Enforcement decision for a browser-agent site (KIR-4704). */
export const browserAgentEnforcementSchema = z.enum([
    'approve',
    'block',
    'monitor',
]);
/**
 * Agent types Kirin can actually block (KIR-4703/4704). Only the CLI-hook
 * channels reach the PreToolUse hook and can be denied there; `chrome_via_browser`
 * is disk-observed browser-internal use with no local choke point, so it is
 * Monitor-only and a `block` decision for it is unenforceable (rejected below,
 * and filtered from the agent blocklist). Single source of truth for both.
 */
export const BROWSER_AGENT_ENFORCEABLE_TYPES = [
    'chrome_via_cli',
    'claude_code_cli',
];
export function isEnforceableBrowserAgentType(agentType) {
    return BROWSER_AGENT_ENFORCEABLE_TYPES.includes(agentType);
}
/**
 * Blocklist the agent fetches to enforce (KIR-4703). One entry per blocked
 * (agentType, host); the hook denies a matching browser/web tool call for the
 * enforceable agent types.
 */
export const browserBlocklistEntrySchema = z.object({
    agentType: z.string(),
    host: z.string(),
});
export const browserBlocklistResponseSchema = z.object({
    entries: z.array(browserBlocklistEntrySchema),
});
/**
 * Read model for the Browser Agent Sites view (KIR-4704, spec 70509). One row
 * per (site × agent type); `visits30d` is the rolling-30-day sum, `users` the
 * distinct users with in-window activity, `windowComplete` false → "so far".
 */
export const browserAgentSitesViewSiteSchema = z.object({
    siteHost: z.string(),
    agentType: z.enum([
        'chrome_via_cli',
        'chrome_via_browser',
        'claude_code_cli',
    ]),
    agentName: z.string(),
    reachedVia: z.string(),
    agentExtId: z.string().nullable(),
    users: z.number().int(),
    visits30d: z.number().int(),
    lastSeen: z.string(),
    sources: z.array(z.string()),
    windowComplete: z.boolean(),
    // Enforcement decision (Approve/Block/Monitor) + approval record, from the
    // browser-agent-sites policy_exceptions rows. null = not reviewed.
    enforcement: browserAgentEnforcementSchema.nullable(),
    approval: z.object({ by: z.string(), date: z.string() }).nullable(),
});
export const browserAgentSitesViewResponseSchema = z.object({
    /** One page of rows, after the query's filters and sort. */
    sites: z.array(browserAgentSitesViewSiteSchema),
    /** Tab counts over every row (not narrowed by search, Show filter or page). */
    counts: z.object({
        all: z.number().int(),
        chrome_via_cli: z.number().int(),
        chrome_via_browser: z.number().int(),
        claude_code_cli: z.number().int(),
    }),
    /** `total` is the filtered row count the pages walk through. */
    pagination: paginationResponseSchema,
});
/** Columns the Browser Agent Sites view can sort by (server-side, KIR-4704). */
export const browserAgentSitesSortFieldSchema = z.enum([
    'siteHost',
    'agentName',
    'reachedVia',
    'users',
    'visits30d',
    'lastSeen',
]);
/** Columns the Browser Agent Sites table can filter on (KIR-4704). */
export const BROWSER_AGENT_SITES_FILTERABLE_FIELDS = {
    SITE_HOST: 'siteHost',
    AGENT_NAME: 'agentName',
    REACHED_VIA: 'reachedVia',
    USERS: 'users',
    VISITS_30D: 'visits30d',
    LAST_SEEN: 'lastSeen',
    ENFORCEMENT: 'enforcement',
    APPROVAL: 'approval',
    // Not a table column: the agent-type key, filtered by the public API
    // (KIR-5144) where the Agent / Via labels are display copy, not keys.
    AGENT_TYPE: 'agentType',
};
export const BROWSER_AGENT_SITES_FILTER_COLUMN_DATA_TYPES = {
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.SITE_HOST]: ColumnDataTypes.STRING,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_NAME]: ColumnDataTypes.ENUM,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.REACHED_VIA]: ColumnDataTypes.ENUM,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.NUMBER,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.VISITS_30D]: ColumnDataTypes.NUMBER,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.LAST_SEEN]: ColumnDataTypes.DATE,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.ENFORCEMENT]: ColumnDataTypes.ENUM,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.APPROVAL]: ColumnDataTypes.ENUM,
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_TYPE]: ColumnDataTypes.ENUM,
};
// Every row has a site, agent, via, counts and last-seen, so the value-less
// empty operators never narrow anything; Site is text-only (no option list).
export const BROWSER_AGENT_SITES_OPERATOR_OVERRIDES = {
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.SITE_HOST]: [
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
    ],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_NAME]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.REACHED_VIA]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.USERS]: [FilterOperators.INCLUDES],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.VISITS_30D]: [
        FilterOperators.INCLUDES,
    ],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.LAST_SEEN]: [...DATE_RANGE_OPERATORS],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.ENFORCEMENT]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.APPROVAL]: [FilterOperators.INCLUDES],
    [BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_TYPE]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
};
/** Enforcement filter value for a site with no decision yet ("Not set"). */
export const BROWSER_AGENT_ENFORCEMENT_NOT_SET = 'none';
export const browserAgentEnforcementFilterValueSchema = z.enum([
    'approve',
    'block',
    'monitor',
    BROWSER_AGENT_ENFORCEMENT_NOT_SET,
]);
/** Approval filter values: a decision was made, or the site is not reviewed. */
export const browserAgentApprovalFilterValueSchema = z.enum([
    'reviewed',
    'not_reviewed',
]);
/** Fields whose filter options come from the server (distinct values in use). */
export const browserAgentSitesFilterOptionFieldSchema = z.enum([
    BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_NAME,
    BROWSER_AGENT_SITES_FILTERABLE_FIELDS.REACHED_VIA,
]);
export const browserAgentSitesFilterOptionsQuerySchema = z.object({
    field: browserAgentSitesFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const browserAgentSitesFilterOptionsResponseSchema = z.object({
    field: browserAgentSitesFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const browserAgentSitesQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: browserAgentSitesSortFieldSchema.default('lastSeen'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    // Pre-table single filters, still served (e2e + API callers): agent type and
    // the enforcement decision. The table sends column `filters` instead.
    agentType: z
        .enum(['chrome_via_cli', 'chrome_via_browser', 'claude_code_cli'])
        .optional(),
    enforcement: browserAgentEnforcementSchema.optional(),
    filters: FiltersSchema.optional(),
});
/**
 * Query for the paginated Browser Agent Sites view (KIR-4704): page/limit/search,
 * sort, and the shared-table column `filters`. Filters and sort run server-side,
 * before the page is cut, so every page is correct.
 */
export const browserAgentSitesQuerySchema = createTableQuerySchema(browserAgentSitesQueryBaseSchema, BROWSER_AGENT_SITES_FILTER_COLUMN_DATA_TYPES, BROWSER_AGENT_SITES_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        const enumSchema = filter.field === BROWSER_AGENT_SITES_FILTERABLE_FIELDS.ENFORCEMENT
            ? browserAgentEnforcementFilterValueSchema
            : filter.field === BROWSER_AGENT_SITES_FILTERABLE_FIELDS.APPROVAL
                ? browserAgentApprovalFilterValueSchema
                : filter.field === BROWSER_AGENT_SITES_FILTERABLE_FIELDS.LAST_SEEN
                    ? z.string().datetime({ offset: true, local: true })
                    : filter.field === BROWSER_AGENT_SITES_FILTERABLE_FIELDS.AGENT_TYPE
                        ? browserAgentSitesViewSiteSchema.shape.agentType
                        : null;
        if (!enumSchema)
            return;
        filter.value.forEach((v, valueIndex) => {
            if (!enumSchema.safeParse(v).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Invalid ${filter.field} filter value`,
                    path: ['filters', index, 'value', valueIndex],
                });
            }
        });
    });
});
/** Client-side params for the view (serialized with the table query helper). */
export const browserAgentSitesViewParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: browserAgentSitesSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    agentType: z
        .enum(['chrome_via_cli', 'chrome_via_browser', 'claude_code_cli'])
        .optional(),
    enforcement: browserAgentEnforcementSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
});
/** Query for one site's Users drill-down (KIR-4704 review #15). */
export const browserAgentSiteUsersQuerySchema = z.object({
    host: browserAgentSiteHostSchema,
    agentType: z.enum([
        'chrome_via_cli',
        'chrome_via_browser',
        'claude_code_cli',
    ]),
});
/**
 * Set (or clear) a site's enforcement decision (KIR-4704). `action: null`
 * un-reviews the site (removes the decision).
 */
export const setBrowserAgentDecisionRequestSchema = z
    .object({
    agentType: z.enum([
        'chrome_via_cli',
        'chrome_via_browser',
        'claude_code_cli',
    ]),
    host: browserAgentSiteHostSchema,
    action: browserAgentEnforcementSchema.nullable(),
})
    .superRefine((val, ctx) => {
    // KIR-4704: Block is only offered where Kirin can enforce it. Reject a Block
    // on a non-enforceable agent type (chrome_via_browser is Monitor-only) so a
    // direct API call cannot create an unenforceable decision the UI forbids.
    if (val.action === 'block' &&
        !isEnforceableBrowserAgentType(val.agentType)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['action'],
            message: 'Block is not available for this agent type; browser-internal use cannot be enforced. Use Monitor.',
        });
    }
});
export const shadowAiToolListItemSchema = z.object({
    name: z.string(),
    toolId: z.string(),
    category: z.string(),
    sources: z.array(z.string()),
    version: z.string().nullable(),
    userCount: z.number().int().min(0),
    addedAt: z.string().datetime().nullable(),
    lastUsed: z.string().datetime().nullable(),
    extras: z.record(z.string(), z.string()).nullable(),
    riskLevel: severitySchema,
});
export const shadowAiToolListResponseSchema = z.object({
    tools: z.array(shadowAiToolListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
/**
 * KIR-4650: the live process behind an installed-tool detection, when the tool
 * happens to be running on that endpoint. Read from the running-processes
 * table — installed-tool reports carry no pid or command line of their own.
 *
 * `commandLine` is redacted of secrets on the endpoint before it is ever
 * uploaded (the agent's `RedactCommandLineSecrets`), so it is safe to display.
 */
export const shadowAiToolUserProcessSchema = z.object({
    pid: z.number().int(),
    ppid: z.number().int().nullable(),
    binaryPath: z.string().nullable(),
    commandLine: z.string().nullable(),
    isRoot: z.boolean(),
    parentName: z.string().nullable(),
});
export const shadowAiToolUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    machineId: z.string(),
    // KIR-3035: resolved endpoint (machine) name for machineId; shown in the
    // renamed "Endpoint" column. Optional/nullable when no endpoint is registered.
    endpointName: z.string().nullable().optional(),
    timestamp: z.string().datetime(),
    /** Logged-in account extracted for this tool on this user's machine, if any. */
    loggedInUser: loggedInUserSchema.nullish(),
    /** Backend-computed relationship to {@link email}; defaults to `unknown`. */
    loggedInUserMatch: loggedInUserMatchSchema.default('unknown'),
    /** Reason a LoggedInUser is absent, when {@link loggedInUser} is null. */
    loggedInUserReason: loggedInUserReasonSchema.nullish(),
    /**
     * KIR-4650: which collectors matched this tool on this endpoint (`bin`,
     * `config`, `app`, …). Generic rules such as "MCP CLI" match any binary
     * named `mcp`, so how it was found is half of what lets a customer triage
     * the finding. Defaulted so a response predating the field still parses.
     */
    sources: z.array(z.string()).default([]),
    /**
     * KIR-4650: the filesystem paths the detection matched on, for this
     * endpoint. Not positionally related to {@link sources} — the agent
     * consolidates both into flat lists, so path _n_ is not source _n_.
     */
    paths: z.array(z.string()).default([]),
    /** The tool's live process here, or null when it is installed but not running. */
    runningProcess: shadowAiToolUserProcessSchema.nullish(),
});
/**
 * KIR-5296: a tool can fill one table row per category, in Browser or
 * Installed AI Tools. These narrow its user list to one such row; omitted,
 * the list covers every row of the tool (dashboard widgets).
 */
export const SHADOW_AI_TOOL_USERS_SOURCES = ['browser', 'installed'];
export const shadowAiToolUsersQuerySchema = z.object({
    // KIR-3495: narrow to one member on the user-scope page.
    scopeUserId: z.string().uuid().optional(),
    category: z.string().min(1).max(64).optional(),
    source: z.enum(SHADOW_AI_TOOL_USERS_SOURCES).optional(),
});
export const shadowAiToolUsersResponseSchema = z.object({
    toolId: z.string(),
    users: z.array(shadowAiToolUserSchema),
});
/**
 * The users behind one Browser Agent Sites row (KIR-4704): one row per user
 * endpoint active in the last 30 days, in the Shadow AI tool-user shape so the
 * popup reuses the same users table.
 */
export const browserAgentSiteUsersResponseSchema = z.object({
    users: z.array(shadowAiToolUserSchema),
});
export const shadowAiTopUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    installedCount: z.number().int().min(0),
    browserCount: z.number().int().min(0),
    addedAt: z.string().datetime().nullable(),
    extras: z.record(z.string(), z.string()).nullable(),
});
export const shadowAiTopUsersResponseSchema = z.object({
    users: z.array(shadowAiTopUserSchema),
});
export const shadowAiTopToolSchema = z.object({
    toolId: z.string(),
    name: z.string(),
    category: z.string(),
    riskLevel: severitySchema,
    userCount: z.number().int().min(0),
    sources: z.array(z.string()),
    addedAt: z.string().datetime().nullable(),
    lastUsed: z.string().datetime().nullable(),
});
export const shadowAiTopToolsResponseSchema = z.object({
    tools: z.array(shadowAiTopToolSchema),
    totalCount: z.number().int().min(0),
});
export const runningProcessListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    toolId: z.string(),
    category: z.string(),
    user: z.string(),
    userId: z.string().uuid(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    pid: z.number().int(),
    ppid: z.number().int().nullable(),
    port: z.number().int().nullable(),
    binaryPath: z.string().nullable(),
    commandLine: z.string().nullable(),
    parentName: z.string().nullable(),
    isRoot: z.boolean(),
    extras: z.record(z.string(), z.string()).nullable(),
    startedAt: z.string().datetime({ offset: true }),
});
export const runningProcessListResponseSchema = z.object({
    processes: z.array(runningProcessListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    lastHeartbeat: z.string().datetime().nullable(),
});
export const shadowAiOrgStatsSchema = z.object({
    totalTools: z.number().int().min(0),
    highRiskCount: z.number().int().min(0),
    rootProcessCount: z.number().int().min(0),
    newThisWeekCount: z.number().int().min(0),
    activeProcesses: z.number().int().min(0),
    aiConnectionsCount: z.number().int().min(0),
    pendingReviewCount: z.number().int().min(0),
});
export const networkConnectionStatusSchema = z.enum([
    'approved',
    'flagged',
    'pending',
    'ignored',
]);
export const shadowAiConfidenceLabelSchema = z.enum([
    'likely_ai',
    'possible_ai',
    'unlikely_ai',
]);
export const shadowAiConfidenceTagSchema = z.enum([
    'streaming',
    'programmatic',
    'ai_pattern',
    'infra',
]);
export const shadowAiConfidenceSchema = z.object({
    score: z.number().int().min(0).max(99),
    label: shadowAiConfidenceLabelSchema,
    tags: z.array(shadowAiConfidenceTagSchema),
});
export const confidenceLabelLabels = {
    likely_ai: 'Likely AI',
    possible_ai: 'Possible AI',
    unlikely_ai: 'Unlikely AI',
};
export const networkConnectionListItemSchema = z.object({
    processName: z.string().nullable(),
    /**
     * The process that drove the socket owner — for browser-driven agents the
     * owner is a generic helper ("Google Chrome Helper") and this names the
     * real agent. The "via …" line prefers it over {@link processName}. Null
     * until KIR-4674 populates it (KIR-4588).
     */
    parentProcessName: z.string().nullable(),
    domain: z.string(),
    status: networkConnectionStatusSchema,
    ignoreSource: z.enum(['global', 'org']).nullable(),
    identificationSource: z.enum(['global', 'org']).nullable(),
    hitCount: z.number().int(),
    /**
     * Hosts rolled up under this row's catalog service key (KIR-4838) — the
     * public-suffix answer adjusted by the curated catalog's splits and merges,
     * which is not always the registrable domain. Always 1 while domain grouping
     * is off, since each row is then a single host.
     */
    hostCount: z.number().int(),
    /**
     * This row is a catalog SERVICE, not a single host (KIR-4948) — so `domain`
     * is the service key (`zoom.us`), which may well be a name no endpoint ever
     * connected to, and the hosts behind it come from the hosts endpoint.
     *
     * Deliberately NOT inferred from `hostCount > 1`, which is what the UI used
     * to do and which is wrong in both directions for the same row. `hostCount`
     * counts the hosts THIS VIEWER's events reach, so a service with one visible
     * host — or one whose host rows the backfill has not written yet, which
     * counts zero — read as ungrouped: the expansion then labelled the service
     * key "Destination host", and the per-host Status menu, `New` flag and
     * Classification panel were unreachable for it. The server knows which grain
     * it answered in; the client cannot work it out.
     *
     * Optional, NOT defaulted — unlike the other fields added here, and the
     * difference matters. Frontend and API deploy separately, so a new client
     * parses old responses for the length of a rollout, and grouping already
     * ships (KIR-4838): an API one revision behind serves genuinely grouped rows
     * and simply does not mention it. Defaulting that silence to `false` would
     * tell the client every one of those services is a single host — no host
     * sub-table, the service key labelled as the destination, users mixed back
     * across member hosts — which is a worse answer than the `hostCount > 1`
     * guess the client was making before this field existed.
     *
     * So `undefined` means "this API does not say", and the caller falls back to
     * that guess; see `isGroupedRow`. Once the API has rolled out the field is
     * always present and the guess is never reached.
     */
    grouped: z.boolean().optional(),
    /**
     * The hosts under this row do not all resolve to the same status, so the row
     * shows `Mixed` rather than one of them (KIR-4948). "Resolve" is the
     * effective status — the backend's `resolvedHostDecisionSql`: the host's
     * override, then the service's decision, then Kirin's built-in lists
     * (KIR-5168) — so a host that merely inherits still counts, and a domain
     * classified Identified with one host left Unreviewed reads as Mixed.
     *
     * Deliberately a separate flag rather than a member of
     * {@link networkConnectionStatusSchema}: `status` is a filterable enum
     * backing the per-column filters and the four scope tabs, and a fifth value
     * would change which bucket a row falls into as well as how it reads.
     *
     * Always false while domain grouping is off, where a row is a single host and
     * cannot disagree with itself.
     *
     * Defaulted rather than required so the field can be added without a
     * lockstep deploy: the frontend and the API ship as separate services, so a
     * new client parses old responses for the length of a rollout, and a
     * required boolean would fail the whole list rather than one column. `false`
     * is also what a backend that cannot answer means -- no evidence the hosts
     * disagree. The default keeps the parsed type `boolean`, so consumers never
     * see `undefined`.
     */
    hostStatusMixed: z.boolean().default(false),
    uniqueUsers: z.number().int(),
    uniqueMachines: z.number().int(),
    totalBytesIn: z.number().int(),
    totalBytesOut: z.number().int(),
    ja4Fingerprints: z.array(z.string()),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    isLongLived: z.boolean().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
    classifiedBy: z.string().nullable(),
    classifiedByEmail: z.string().nullable(),
    classifiedByName: z.string().nullable(),
    classifiedAt: z.string().datetime().nullable(),
    identifiedAgentName: z.string().nullable(),
    confidence: shadowAiConfidenceSchema.optional().nullable(),
    // KIR-5098: endpoints matching the active endpoint filter (labels capped at
    // 3, count = all); absent without that filter.
    matchedEndpoints: z.array(z.string()).optional(),
    matchedEndpointCount: z.number().int().min(0).optional(),
});
export const networkConnectionsResponseSchema = z.object({
    connections: z.array(networkConnectionListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(1000),
        total: z.number().int().min(0),
        totalPages: z.number().int().min(0),
    }),
});
export const networkConnectionUserSchema = z.object({
    userId: z.string(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    machineId: z.string(),
    // KIR-3035: resolved endpoint (machine) name for machineId; shown in the
    // renamed "Endpoint" column. Optional/nullable when no endpoint is registered.
    endpointName: z.string().nullable().optional(),
    // Null when the collector could not attribute a process; shown as Unknown.
    // Only reachable on the domain-wide list — a process-scoped query excludes
    // unattributed rows (KIR-4314).
    processName: z.string().nullable(),
    /**
     * Process and connection detail for the expanded finding (KIR-4588). Null
     * is routine, not an error: `signer` is empty for unsigned binaries and
     * always on Linux, and `dstIp`/`port` exist only for macOS TLS findings.
     * The UI omits null lines rather than rendering placeholders.
     */
    pid: z.number().int().nullable(),
    processPath: z.string().nullable(),
    signerStatus: signatureStatusSchema.nullable(),
    signer: z.string().nullable(),
    localUser: z.string().nullable(),
    dstIp: z.string().nullable(),
    port: z.number().int().nullable(),
    /** Capture transport: "tls" | "dns" | "streaming" | "etw_dns". */
    source: z.string().nullable(),
    hitCount: z.number().int(),
    bytesIn: z.number().int().nullable(),
    bytesOut: z.number().int().nullable(),
    ja4: z.string().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const networkConnectionUsersResponseSchema = z.object({
    users: z.array(networkConnectionUserSchema),
});
/**
 * The closed set a host override can hold. A host is either classified as an
 * agent or dismissed; "no override" is expressed by `null` rather than by a
 * third member, so `decision` and `effectiveDecision` do not share a type.
 */
export const networkDomainHostDecisionSchema = z.enum([
    'identified',
    'dismissed',
]);
/**
 * The host's own override, else the service's decision, else Kirin's built-in
 * lists, resolved server-side (KIR-5168), so it also carries the unreviewed
 * state that a host override itself cannot hold.
 */
export const networkDomainHostEffectiveDecisionSchema = z.enum([
    'identified',
    'dismissed',
    'unreviewed',
]);
/**
 * What decided a host's `effectiveDecision` (KIR-5168): a reviewer's override
 * on the host itself, the service's decision, or Kirin's built-in lists.
 */
export const networkDomainHostEffectiveSourceSchema = z.enum([
    'override',
    'domain',
    'builtin',
]);
/**
 * One host under a grouped Network Discovery row (KIR-4838).
 *
 * `decision` is the host's own override and is usually null — `effectiveDecision`
 * is the resolved answer (override, service, then built-in lists) and is what
 * the UI should render. Keeping both lets the UI show that a host was overridden rather than
 * inherited, without re-implementing the precedence rule.
 */
export const networkDomainHostSchema = z.object({
    host: z.string(),
    lastProcessName: z.string().nullable(),
    hitCount: z.number().int(),
    bytesIn: z.number().int(),
    bytesOut: z.number().int(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
    decision: networkDomainHostDecisionSchema.nullable(),
    effectiveDecision: networkDomainHostEffectiveDecisionSchema,
    /**
     * This host appeared under the service AFTER the service was classified, and
     * nobody has decided it since (KIR-4948).
     *
     * It still inherits — `effectiveDecision` already carries the service's
     * answer — so this is a "somebody should look at this" marker shown beside
     * the status, not instead of it. KIR-4588: "apply the classification going
     * forward, but flag a newly seen host for review so nothing is trusted
     * silently."
     *
     * Clears on its own when the service is re-classified or when the host is
     * given its own decision; there is no acknowledge action to build.
     *
     * Derived from the host row's `first_seen`, which the backfill sets to the
     * host's earliest EVENT but which the ingest roll-up can only default to the
     * moment the row was created. Those agree except when a roll-up failed and a
     * later one stamped a host whose traffic was already older, which reads as
     * new when it is not. The error is one-directional — it over-reports hosts to
     * review and never under-reports them — so treat this as "worth a look", not
     * as proof the host is unseen.
     *
     * Defaulted for the same reason as
     * {@link networkConnectionListItemSchema.shape.hostStatusMixed}: a required
     * field added to a response fails the whole payload when a new client meets
     * an API that predates it, and these two services deploy separately. `false`
     * is the right fallback -- an API that cannot answer is not evidence the
     * host is new.
     */
    isNew: z.boolean().default(false),
    /**
     * A REVIEWER singled this host out, as opposed to a domain-level action
     * having mirrored its answer down onto the host (KIR-4948).
     *
     * `decision !== null` is not the same question and must not be used for it.
     * Every fan-out runs `applyHostDecision`, which writes the host's `decision`
     * and leaves `decision_source` NULL — so one "Classify domain" leaves every
     * member host carrying a decision it never chose. Keying the overridden badge
     * off `decision` therefore marked the whole service as dissenting the moment
     * it was classified, which is precisely when the hosts that really do dissent
     * matter most.
     *
     * Only `setHostDecision` sets `decision_source = 'manual'`, and that is also
     * the exact set `applyHostDecision` refuses to touch — so this field is what
     * "a domain-level action will not change it" literally means.
     *
     * `optional`, not `default(false)`: these two services deploy separately, and
     * `undefined` has to stay distinguishable from `false` so a new client
     * talking to an older API can fall back to the old `decision !== null` guess
     * rather than quietly reporting that NO host is overridden — which would hide
     * real overrides instead of over-reporting them. See `isOverriddenHost`.
     */
    overridden: z.boolean().optional(),
    /**
     * What decided `effectiveDecision` (KIR-5168); null while the host is
     * unreviewed. The UI labels the host from it: "AI (manual override)", "AI
     * (builtin)", plain "AI" for a host following its domain.
     *
     * `optional` for the same reason as `overridden`: an older API omits it, and
     * the UI must tell "not sent" from "unreviewed" to fall back to `overridden`.
     */
    effectiveSource: networkDomainHostEffectiveSourceSchema.nullable().optional(),
    /**
     * Who set the status this host shows, and when — its own override if it has
     * one, otherwise whoever classified the service. Same precedence as
     * {@link networkDomainHostSchema.shape.effectiveDecision}, so the attribution
     * always explains the status next to it.
     */
    decidedBy: z.string().nullable().default(null),
    decidedByEmail: z.string().nullable().default(null),
    decidedByName: z.string().nullable().default(null),
    decidedAt: z.string().datetime().nullable().default(null),
    /**
     * The host's own "Possible AI" signal.
     *
     * Computed without the JA4 component, which the service tables do not carry,
     * so it can read marginally lower than the same host does in the ungrouped
     * list (JA4 is 5 of 99 points).
     *
     * Defaulted to null like the fields above so an API that predates it does
     * not fail the whole host list; the badge is already conditional on a value
     * being present.
     */
    confidence: shadowAiConfidenceSchema.nullable().default(null),
    /**
     * Distinct users who reached this host, scoped to current organization
     * members (KIR-4750) -- the same users the host's expanded users table
     * lists, so the two numbers agree. Unlike the rest of this row it is read
     * from the events, not the service tables, which carry no user dimension.
     *
     * Null only from an API that predates the field; the UI then shows "-".
     */
    uniqueUsers: z.number().int().min(0).nullable().default(null),
});
export const networkDomainHostsResponseSchema = z.object({
    hosts: z.array(networkDomainHostSchema),
});
/**
 * Per-host triage (KIR-4838). `decision: null` resets the host to inheriting
 * its service — a domain-level action deliberately leaves overrides alone, so
 * this is the only way back to inherited.
 */
export const networkDomainHostDecisionBodySchema = z.object({
    decision: networkDomainHostDecisionSchema.nullable(),
});
export const networkDomainHostDecisionResponseSchema = z.object({
    host: z.string(),
    decision: networkDomainHostDecisionSchema.nullable(),
    effectiveDecision: networkDomainHostEffectiveDecisionSchema,
    effectiveSource: networkDomainHostEffectiveSourceSchema.nullable().optional(),
});
/**
 * KIR-4314: scopes the expanded row's user list to that row's process. Drops
 * every other process, including unattributed (unknown-process) traffic to the
 * same host. Omit for domain-wide.
 */
export const networkConnectionUsersQuerySchema = z.object({
    processName: z.string().min(1).optional(),
    /**
     * Narrow a grouped row's users to one of its member hosts (KIR-4948).
     *
     * Omitted, the response answers "who used this service", which is what the
     * collapsed row needs. Supplied, it answers "who used this host" — the only
     * question a per-host panel may ask, since a service can roll up dozens of
     * unrelated hosts and no single user speaks for any of them.
     *
     * Applied in both grouping modes. Ungrouped it is simply redundant with the
     * row key, since the row is already a single host.
     */
    host: z.string().min(1).optional(),
});
// Network Discovery / Pending Domains
export const pendingDomainClassificationSchema = z.enum([
    'ai_approved',
    'ai_flagged',
    'ignore',
]);
export const pendingDomainSchema = z.object({
    domain: z.string(),
    hitCount: z.number().int(),
    uniqueUsers: z.number().int(),
    uniqueMachines: z.number().int(),
    sampleProcess: z.string().nullable(),
    sampleJa4: z.string().nullable(),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const pendingDomainsResponseSchema = z.object({
    domains: z.array(pendingDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const pendingDomainUserSchema = z.object({
    userId: z.string(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    machineId: z.string(),
    hitCount: z.number().int(),
    firstSeen: z.string().datetime(),
    lastSeen: z.string().datetime(),
});
export const pendingDomainDetailSchema = pendingDomainSchema.extend({
    users: z.array(pendingDomainUserSchema),
});
export const orgIgnoreDomainSchema = z.object({
    domain: z.string(),
    addedBy: z.string().nullable(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const orgIgnoreDomainsResponseSchema = z.object({
    domains: z.array(orgIgnoreDomainSchema),
});
export const globalIgnoreDomainSchema = z.object({
    domain: z.string(),
    category: z.string().nullable(),
    createdAt: z.string().datetime(),
    isPreset: z.boolean().optional(),
});
export const globalIgnoreDomainsResponseSchema = z.object({
    domains: z.array(globalIgnoreDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const classifiedDomainStatusSchema = z.enum(['approved', 'flagged']);
export const classifiedDomainSchema = z.object({
    id: z.string(),
    domain: z.string(),
    status: classifiedDomainStatusSchema,
    category: z.string().nullable(),
    name: z.string().nullable(),
    notes: z.string().nullable(),
    classifiedBy: z.string().nullable(),
    classifiedByEmail: z.string().nullable(),
    classifiedByName: z.string().nullable(),
    classifiedAt: z.string().datetime(),
});
export const classifiedDomainsResponseSchema = z.object({
    domains: z.array(classifiedDomainSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
export const networkTrafficStatsSchema = z.object({
    totalDomains: z.number().int(),
    pendingCount: z.number().int(),
    approvedCount: z.number().int(),
    flaggedCount: z.number().int(),
    totalBytesIn: z.number().int(),
    totalBytesOut: z.number().int(),
    uniqueJa4Count: z.number().int(),
    uniqueMachines: z.number().int(),
    topJa4: z.array(z.object({
        ja4: z.string(),
        domainCount: z.number().int(),
        hitCount: z.number().int(),
    })),
});
// ============================================================================
// Query Schemas
// ============================================================================
export const shadowAiToolSortFieldSchema = z.enum([
    'name',
    'category',
    'addedAt',
    'lastUsed',
    'userCount',
]);
export const runningProcessSortFieldSchema = z.enum([
    'name',
    'category',
    'user',
    'startedAt',
]);
export const shadowAiSourceTypeSchema = z.enum(['installed', 'browser']);
export const shadowAiToolListBaseQuerySchema = baseListQuerySchema.extend({
    categories: z
        .preprocess(value => typeof value === 'string' ? value.split(',').filter(Boolean) : value, z.array(z.string()))
        .optional(),
    sourceType: shadowAiSourceTypeSchema.optional(),
    sortBy: shadowAiToolSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const shadowAiToolListQuerySchema = shadowAiToolListBaseQuerySchema.extend({
    userId: z.string().uuid().optional(),
});
export const runningProcessListBaseQuerySchema = baseListQuerySchema.extend({
    sortBy: runningProcessSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const runningProcessListQuerySchema = runningProcessListBaseQuerySchema.extend({
    userId: z.string().uuid().optional(),
    isRoot: z.coerce.boolean().optional(),
});
export const networkConnectionSortFieldSchema = z.enum([
    'hitCount',
    'lastSeen',
    'domain',
    'processName',
    'status',
    'totalBytes',
    'uniqueMachines',
    'uniqueUsers',
]);
/**
 * Indicates what kind of client-driven change triggered this request.
 * The backend uses this together with `knownTotal` to decide whether the
 * expensive COUNT query can be skipped:
 *
 * - `filters` (default) — first load, filter/search change, or mutation
 *   invalidation. Always runs COUNT.
 * - `sort` — only the sort changed. The total cannot have changed, so the
 *   backend trusts `knownTotal` and skips COUNT entirely (including the
 *   page-1 recount that `finalizeTotal` normally performs).
 * - `page` — only the page changed. Same as `sort`.
 */
export const networkConnectionsCountScopeSchema = z.enum([
    'filters',
    'sort',
    'page',
]);
// ============================================================================
// Network Connections DataTable filtering (KIR-3496)
// Per-column Filter/Sort/Search on the Shadow AI → Network Discovery table,
// mirroring the Browser AI Tools table. Field ids match the sort-field enum
// (and the `domain_enriched` columns the repository filters on).
// ============================================================================
export const NETWORK_CONNECTIONS_FILTERABLE_FIELDS = {
    PROCESS: 'processName',
    HOST: 'domain',
    STATUS: 'status',
    TRAFFIC: 'totalBytes',
    HITS: 'hitCount',
    USERS: 'uniqueUsers',
    MACHINES: 'uniqueMachines',
    LAST_SEEN: 'lastSeen',
    // KIR-5098: hosts reached from an endpoint matching this name or ID.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const NETWORK_CONNECTIONS_FILTER_COLUMN_DATA_TYPES = {
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.PROCESS]: ColumnDataTypes.STRING,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HOST]: ColumnDataTypes.STRING,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.STATUS]: ColumnDataTypes.ENUM,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.TRAFFIC]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.HITS]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.MACHINES]: ColumnDataTypes.NUMBER,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN]: ColumnDataTypes.DATE,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.ENDPOINT]: ColumnDataTypes.STRING,
};
// Last Seen drops equals/notEquals — range operators + value-less only.
export const NETWORK_CONNECTIONS_OPERATOR_OVERRIDES = {
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN]: DATE_RANGE_OPERATORS,
    [NETWORK_CONNECTIONS_FILTERABLE_FIELDS.ENDPOINT]: ENDPOINT_FILTER_OPERATORS,
};
const networkConnectionsQueryBaseSchema = baseListQuerySchema.extend({
    status: z
        .enum(['all', 'pending', 'approved', 'flagged', 'ignored'])
        .optional(),
    sortBy: networkConnectionSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    countScope: networkConnectionsCountScopeSchema.optional(),
});
export const networkConnectionsQuerySchema = createTableQuerySchema(networkConnectionsQueryBaseSchema, NETWORK_CONNECTIONS_FILTER_COLUMN_DATA_TYPES, NETWORK_CONNECTIONS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === NETWORK_CONNECTIONS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Filter values for lastSeen field must be valid ISO 8601 datetime strings',
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
// ----------------------------------------------------------------------------
// Network Connections filter-options (dynamic dropdowns for process/host)
// ----------------------------------------------------------------------------
export const NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS = {
    PROCESS: 'processName',
    HOST: 'domain',
    // KIR-5098: endpoint suggestions (host / ID) for the Matches list.
    ENDPOINT: ENDPOINT_FILTER_FIELD,
};
export const networkConnectionsFilterOptionFieldSchema = z.enum([
    NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS.PROCESS,
    NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS.HOST,
    NETWORK_CONNECTIONS_FILTER_OPTION_FIELDS.ENDPOINT,
]);
export const networkConnectionsFilterOptionsQuerySchema = z.object({
    field: networkConnectionsFilterOptionFieldSchema,
    // KIR-3496: scope the options to the current status view so the dropdown
    // only offers hosts/processes present in the rows the user is looking at.
    status: z
        .enum(['all', 'pending', 'approved', 'flagged', 'ignored'])
        .optional(),
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
export const networkConnectionsFilterOptionsResponseSchema = z.object({
    field: networkConnectionsFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const pendingDomainsSortFieldSchema = z.enum(['hitCount', 'lastSeen']);
export const pendingDomainsQuerySchema = baseListQuerySchema.extend({
    sortBy: pendingDomainsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
export const globalIgnoreDomainsQuerySchema = baseListQuerySchema;
export const classifiedDomainsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: classifiedDomainStatusSchema.optional(),
});
export const sniEventsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    reviewed: z.enum(['all', 'reviewed', 'unreviewed']).optional(),
});
export const shadowAiAgentsQuerySchema = baseListQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(100).default(20),
    status: z
        .enum(['unreviewed', 'identified', 'dismissed', 'known_tool'])
        .optional(),
    minRiskScore: z.coerce.number().int().min(0).max(100).optional(),
});
// ============================================================================
// Responses + Requests for additional endpoints
// ============================================================================
export const shadowAiSuccessResponseSchema = z.object({
    success: z.boolean(),
});
export const shadowAiDomainSchema = z.string().min(1);
export const shadowAiToolIdSchema = z.string().min(1);
export const shadowAiAgentIdSchema = z.string().uuid();
export const networkDomainsResponseSchema = z.object({
    domains: z.array(z.string()),
});
export const agentNamesResponseSchema = z.object({
    names: z.array(z.object({
        name: z.string(),
        category: z.string().nullable(),
    })),
});
export const pendingDomainClassificationRequestSchema = z.object({
    classification: pendingDomainClassificationSchema,
    organizationId: organizationIdSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
/** Body for org-scoped classify (organizationId from path). */
export const pendingDomainClassificationBodySchema = z.object({
    classification: pendingDomainClassificationSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
export const bulkClassifyDomainsRequestSchema = z.object({
    domains: z.array(z.string()).min(1).max(100),
    classification: pendingDomainClassificationSchema,
    organizationId: organizationIdSchema,
    category: z.string().optional(),
    notes: z.string().optional(),
});
/** Body for org-scoped bulk-classify (organizationId from path). */
export const bulkClassifyDomainsBodySchema = z.object({
    domains: z.array(z.string()).min(1).max(100),
    classification: pendingDomainClassificationSchema,
    category: z.string().optional(),
    notes: z.string().optional(),
    // KIR-3496: agent name for bulk "Identify All" so every classified domain is
    // stored with its agentName (not just dumped into notes).
    name: z.string().optional(),
});
export const bulkClassifyDomainsResponseSchema = z.object({
    success: z.boolean(),
    classified: z.number(),
});
export const orgIgnoreDomainRequestSchema = z.object({
    domain: z.string().min(1),
    notes: z.string().optional(),
});
export const trackGlobalDomainRequestSchema = z.object({
    domain: z.string().min(1),
});
export const classifiedDomainDeleteQuerySchema = z.object({
    organizationId: organizationIdSchema,
});
export const bloomFilterPatternSchema = z.object({
    pattern: z.string(),
    type: z.enum(['exact', 'suffix', 'glob']),
});
export const bloomFiltersResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
    ai_filter: z.string(),
    ignore_filter: z.string(),
    ignore_patterns: z.array(bloomFilterPatternSchema),
    agent_names: z.record(z.string(), z.string()),
    linked_tool_ids: z.record(z.string(), z.string()),
});
export const bloomFilterChecksumResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
});
export const shadowAiDetectionCatalogEntryTypeSchema = z.enum([
    'tool',
    'agent',
    'browser_extension',
    'domain',
    'ide_extension',
    'local_llm',
    'framework',
    'search_engine',
    'ai_browser',
    'desktop_app',
    'cli_tool',
]);
export const shadowAiDetectionCatalogSignaturesSchema = z
    .object({
    aliases: z.array(z.string()).optional(),
    processNames: z.array(z.string()).optional(),
    binaryNames: z.array(z.string()).optional(),
    configPaths: z.array(z.string()).optional(),
    domains: z.array(z.string()).optional(),
    browserExtensionPatterns: z.array(z.string()).optional(),
    ideExtensionIds: z.array(z.string()).optional(),
    // Per-OS overrides merged into the shared maps by the Go loader for the
    // current `runtime.GOOS` only (catalog/catalog.go:LoadDetectionCatalog).
    // Same matching semantics as the shared fields:
    //   - processNames*  : lowercased exact lookup (`.exe`/`.cmd`/`.bat`/`.ps1`
    //     stripped before lookup).
    //   - binaryNames*   : lowercased basename match (`\` → `/` normalized,
    //     same suffix stripping).
    //   - configPaths*   : case-insensitive substring against the lowercased,
    //     `\` → `/` normalized candidate path. Use lowercase Unix-shaped
    //     fragments (e.g. `appdata/roaming/cursor`) — never absolute Windows
    //     paths.
    // Use these instead of polluting the shared arrays when a path/binary
    // genuinely only exists on one OS. Entry-level `platforms` still applies
    // to the whole entry; these per-OS arrays only kick in when the entry
    // already passes `entryRunsOnCurrentPlatform`.
    processNamesWindows: z.array(z.string()).optional(),
    binaryNamesWindows: z.array(z.string()).optional(),
    configPathsWindows: z.array(z.string()).optional(),
    processNamesLinux: z.array(z.string()).optional(),
    binaryNamesLinux: z.array(z.string()).optional(),
    configPathsLinux: z.array(z.string()).optional(),
})
    .strict();
export const shadowAiDetectionCatalogEntrySchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    category: z.string().min(1),
    type: shadowAiDetectionCatalogEntryTypeSchema,
    vendor: z.string().optional(),
    platforms: z.array(z.string()).optional(),
    notes: z.string().optional(),
    linkedToolId: z.string().optional(),
    signatures: shadowAiDetectionCatalogSignaturesSchema.optional(),
})
    .strict();
export const shadowAiDetectionCatalogResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
    entries: z.array(shadowAiDetectionCatalogEntrySchema),
});
export const shadowAiDetectionCatalogChecksumResponseSchema = z.object({
    version: z.string(),
    checksum: z.string(),
});
export const sniEventSchema = z.object({
    id: z.string(),
    domain: z.string(),
    source: z.string(),
    hitCount: z.number(),
    userCount: z.number(),
    isProgrammatic: z.boolean().nullable(),
    isStreaming: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    reviewed: z.boolean(),
});
export const sniEventsResponseSchema = z.object({
    events: z.array(sniEventSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        hasMore: z.boolean(),
    }),
});
export const shadowAiDomainClassificationSchema = z.enum([
    'ai_agent',
    'not_ai',
]);
export const shadowAiClassifyDomainRequestSchema = z.object({
    organizationId: organizationIdSchema,
    domain: z.string().min(1),
    classification: shadowAiDomainClassificationSchema,
    category: z.string().optional(),
    name: z.string().optional(),
    notes: z.string().optional(),
});
export const shadowAiAgentStatusSchema = z.enum([
    'unreviewed',
    'identified',
    'dismissed',
    'known_tool',
]);
export const shadowAiAgentSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userEmail: z.string().nullable(),
    userName: z.string().nullable(),
    machineId: z.string(),
    processName: z.string().nullable(),
    processPath: z.string().nullable(),
    ppid: z.number().nullable(),
    parentProcessName: z.string().nullable(),
    ja4Fingerprint: z.string().nullable(),
    status: shadowAiAgentStatusSchema,
    riskScore: z.number(),
    linkedToolId: z.string().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
    totalCalls: z.number(),
    totalBytesIn: z.number(),
    totalBytesOut: z.number(),
    llmDomains: z.array(z.string()),
});
export const shadowAiAgentCallSchema = z.object({
    domain: z.string(),
    hitCount: z.number(),
    bytesIn: z.number(),
    bytesOut: z.number(),
    isStreaming: z.boolean().nullable(),
    isProgrammatic: z.boolean().nullable(),
    isAsymmetric: z.boolean().nullable(),
    isLongLived: z.boolean().nullable(),
    tlsVersion: z.string().nullable(),
    firstSeen: z.string(),
    lastSeen: z.string(),
});
export const shadowAiAgentsResponseSchema = z.object({
    agents: z.array(shadowAiAgentSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        hasMore: z.boolean(),
    }),
});
export const shadowAiAgentDetailSchema = shadowAiAgentSchema.extend({
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    notes: z.string().nullable(),
    calls: z.array(shadowAiAgentCallSchema),
});
export const shadowAiAgentStatusUpdateSchema = z.object({
    status: shadowAiAgentStatusSchema,
    notes: z.string().optional(),
    linkedToolId: z.string().optional(),
});
//# sourceMappingURL=shadow-ai.js.map